import { Button } from '../../../components/buttons'
import { colorPalette } from '../../../style/colorPalette'
import ReloadIcon from '../../../components/icons/ReloadIcon'
import { MonthSelect } from '../../../components/toolbars/common'
import { IconButton, styled, Typography } from '@mui/material'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import { useCallback, useState, useEffect } from 'react'
import DateVO from '../../../../vo/DateVO'
import { intl } from '../../../../i18n'
import { useHistory } from 'react-router'
import SearchConditionDialog from '../components/SearchConditionDialog'
import {
  TaskActualWorkApprovalStatusSearchCondition,
  getDefaultSearchCondition,
} from '../hooks/useSearchCondition'

import { ColDef } from 'ag-grid-community'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'
import FilterButton from '../../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../../containers/BulkSheetView/components/header/ColumnSettingButton'
import SettingSpeedDial from '../../../components/headers/HeaderBar/SettingSpeedDial'
import { IconGroup } from '../../../components/headers/HeaderBar/PageHeader'
import { DecimalAndSexagesimalScale } from '../../../../lib/functions/decimalAndSexagesimalScale'
import EndDateSelector, {
  EndDatePreset,
} from '../../ProjectTaskActualWorkApprovalStatus/Header/EndDateSelector'
import { DecimalAndSexagesimalScaleToggleButtonGroup } from '../../../components/inputs/ToggleButtonGroup/DecimalAndSexagesimalScaleToggleGroup'

type Props = {
  loading: boolean
  refresh: () => void
  onMonthChange: (year: number, month: number) => void
  currentYear?: number
  currentMonth?: number
  searchCondition?: TaskActualWorkApprovalStatusSearchCondition
  onSearchConditionChange?: (
    condition: Partial<TaskActualWorkApprovalStatusSearchCondition>
  ) => void
  onSearch?: () => void
  recordCount?: number
  filteredColumns?: ColDef[]
  onDeleteFilteredColumn?: (column: ColDef) => void
  resetFilters?: () => void
  sortColumnsState?: SortedColumnState[]
  onDeleteSortedColumn?: (colId: string | ColDef<any>) => void
  onDeleteSortedAllColumns?: () => void
  onChangeSortColumnState?: (
    colId: string | ColDef<any>,
    sort: 'asc' | 'desc' | null
  ) => void
  onClickExport?: () => void
  onClickColumnSettingButton?: (e: any) => void
  columnSettingOpen?: boolean
  rowHeight?: number
  onChangeRowHeight?: (value: number) => void
  userCount?: number
  decimalScaleUnit?: DecimalAndSexagesimalScale
  onChangeDecimalScaleUnit?: (value: DecimalAndSexagesimalScale) => void
  endDate?: DateVO
  onEndDateChange?: (date: DateVO) => void
  isCurrentMonth?: boolean
  endDatePreset?: EndDatePreset
  setEndDatePreset?: (preset: EndDatePreset) => void
}

const Header = ({
  loading,
  refresh,
  onMonthChange,
  currentYear,
  currentMonth,
  searchCondition,
  onSearchConditionChange,
  onSearch,
  userCount,
  filteredColumns = [],
  onDeleteFilteredColumn,
  resetFilters,
  sortColumnsState = [],
  onDeleteSortedColumn,
  onDeleteSortedAllColumns,
  onChangeSortColumnState,
  onClickExport,
  onClickColumnSettingButton,
  columnSettingOpen = false,
  rowHeight,
  onChangeRowHeight,
  decimalScaleUnit,
  onChangeDecimalScaleUnit,
  endDate,
  onEndDateChange,
  isCurrentMonth,
  endDatePreset,
  setEndDatePreset,
}: Props) => {
  const now = DateVO.now()
  const history = useHistory()
  const [currentDate, setCurrentDate] = useState<DateVO>(() => {
    if (currentYear && currentMonth) {
      return new DateVO(
        `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`
      )
    }
    return now
  })
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)

  useEffect(() => {
    if (currentYear && currentMonth) {
      const newDate = new DateVO(
        `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`
      )
      setCurrentDate(newDate)
    }
  }, [currentYear, currentMonth])

  const handleMonthChange = useCallback(
    (searchCondition: { year: number; month: number }) => {
      const newDate = new DateVO(
        `${searchCondition.year}-${searchCondition.month}-01`
      )
      setCurrentDate(newDate)
      onMonthChange(searchCondition.year, searchCondition.month)
    },
    [onMonthChange]
  )

  const handlePreviousMonth = useCallback(() => {
    const prevMonth = currentDate.subtractMonths(1)
    setCurrentDate(prevMonth)
    onMonthChange(prevMonth.getYear(), prevMonth.getMonth())
  }, [currentDate, onMonthChange])

  const handleNextMonth = useCallback(() => {
    const nextMonth = currentDate.addMonths(1)
    setCurrentDate(nextMonth)
    onMonthChange(nextMonth.getYear(), nextMonth.getMonth())
  }, [currentDate, onMonthChange])

  const handleThisMonth = useCallback(() => {
    const thisMonth = DateVO.now()
    setCurrentDate(thisMonth)
    onMonthChange(thisMonth.getYear(), thisMonth.getMonth())
  }, [onMonthChange])

  const handleOpenSearchDialog = useCallback(() => {
    setSearchDialogOpen(true)
  }, [])

  const handleCloseSearchDialog = useCallback(() => {
    setSearchDialogOpen(false)
  }, [])

  const resetSearchCondition = useCallback(() => {
    onSearchConditionChange?.(getDefaultSearchCondition())
  }, [onSearchConditionChange])

  const handleTransitionToProjectClosingStatus = useCallback(() => {
    history.push('/projectClosingStatus/taskActualWork')
  }, [history])

  return (
    <HeaderContainer>
      <Button onClick={refresh} disabled={loading} sx={refreshButtonStyle}>
        <ReloadIcon />
      </Button>

      {searchCondition && onSearchConditionChange && onSearch && (
        <Button
          onClick={handleOpenSearchDialog}
          colorPattern="skyBlue"
          disabled={loading}
          sx={SearchConditionStyle}
        >
          {intl.formatMessage({
            id: 'taskActualWorkApprovalStatus.detailSearch',
          })}
        </Button>
      )}
      <MonthNavigation>
        <MonthNavigationButtons>
          <ShiftButton onClick={handlePreviousMonth} disabled={loading}>
            <ArrowBackIos style={arrowIconStyle} />
          </ShiftButton>

          <MonthSelect
            year={currentDate.getYear()}
            month={currentDate.getMonth()}
            search={handleMonthChange}
          />

          <ShiftButton onClick={handleNextMonth} disabled={loading}>
            <ArrowForwardIos style={arrowIconStyle} />
          </ShiftButton>

          <ThisMonthButton onClick={handleThisMonth} disabled={loading}>
            {intl.formatMessage({ id: 'this.month' })}
          </ThisMonthButton>
        </MonthNavigationButtons>
      </MonthNavigation>
      <Typography sx={{ color: colorPalette.monotone[5] }}>
        {intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.periodEndDate',
        })}
        ：
      </Typography>
      {onEndDateChange && endDate && (
        <EndDateSelector
          value={endDate}
          onChange={onEndDateChange}
          preset={endDatePreset}
          onPresetChange={setEndDatePreset}
          disabled={loading || !isCurrentMonth}
          targetMonth={currentDate}
        />
      )}
      {decimalScaleUnit !== undefined && onChangeDecimalScaleUnit && (
        <DecimalAndSexagesimalScaleToggleButtonGroup
          value={decimalScaleUnit}
          onChange={onChangeDecimalScaleUnit}
        />
      )}
      {filteredColumns && onDeleteFilteredColumn && resetFilters && (
        <FilterButton
          filteredColumns={filteredColumns}
          onDeleteFilteredColumn={onDeleteFilteredColumn}
          resetFilters={resetFilters}
        />
      )}

      {sortColumnsState &&
        onDeleteSortedColumn &&
        onDeleteSortedAllColumns &&
        onChangeSortColumnState && (
          <SortButton
            onDeleteSortedColumn={onDeleteSortedColumn}
            onDeleteSortedAllColumns={onDeleteSortedAllColumns}
            onChangeSortColumnState={onChangeSortColumnState}
            sortColumnsState={sortColumnsState}
          />
        )}

      {onClickColumnSettingButton && (
        <div style={{ marginTop: '4px' }}>
          <ColumnSettingButton
            onClick={e => onClickColumnSettingButton(e)}
            open={columnSettingOpen}
          />
        </div>
      )}

      <IconGroup>
        <SettingSpeedDial
          onClickExport={onClickExport}
          rowHeight={rowHeight}
          onClickChangeRowHeight={onChangeRowHeight}
        />
      </IconGroup>

      {searchCondition && onSearchConditionChange && onSearch && (
        <SearchConditionDialog
          open={searchDialogOpen}
          onClose={handleCloseSearchDialog}
          searchCondition={searchCondition}
          onChangeCondition={onSearchConditionChange}
          resetCondition={resetSearchCondition}
          search={onSearch}
          userCount={userCount}
        />
      )}
      <Button
        onClick={handleTransitionToProjectClosingStatus}
        colorPattern="yellow"
        disabled={loading}
        sx={TransitionStyle}
      >
        {intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.transitionProjectClosingStatus',
        })}
      </Button>
    </HeaderContainer>
  )
}

const TransitionStyle = {
  border: 'none',
  cursor: 'pointer',
  fontSize: '12px',
  height: '28px',
  marginLeft: '30px',
}

const SearchConditionStyle = {
  border: 'none',
  cursor: 'pointer',
  fontSize: '12px',
  height: '28px',
  marginRight: '10px',
}

const MonthNavigation = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const MonthNavigationButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const ShiftButton = styled(IconButton)({
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  width: '24px',
  height: '24px',
  padding: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const ThisMonthButton = styled(Button)({
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  minWidth: '48px',
  height: '28px',
  fontSize: '12px',
  color: colorPalette.monotone[4],
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const refreshButtonStyle = {
  background: colorPalette.monotone[1],
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  marginRight: '10px',
  padding: '5px 8px',
  cursor: 'pointer',
  '&:hover': { background: colorPalette.monotone[1] },
}

const HeaderContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginBottom: '4px',
  marginTop: '4px',
  marginLeft: '10px',
})

const arrowIconStyle = {
  color: colorPalette.monotone[5],
  fontSize: '14px',
}

export default Header
